// V1.37.8 / V1.34.3 · Recorte manual con 4 esquinas ajustables (perspectiva real).
// - El archivo ORIGINAL en OneDrive nunca se modifica.
// - La configuración (mode + corners + filter + rotation) se guarda en Mongo
//   sobre `corporate_files.crop_config` y se aplica al componer el PDF.
// - Regla: ORIGINAL > MANUAL > AUTOMATISMOS. Todo es opcional.
// - Auto-detección es opt-in (botón dentro del modal), nunca automática.
//
// Uso:
//   <ManualCropDialog
//     open={open} onClose={...}
//     studentId={sid} fileId={fid}
//     filename="dni_anverso.jpg"
//     onSaved={(cfg) => ...}   // se llama tras guardar
//   />
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Loader2, Undo2, Scan, RotateCw, Save, ImageOff, Sparkles } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";

const HANDLE_SIZE = 14; // radio del círculo (px en canvas coords)
const CANVAS_MAX_W = 820;
const CANVAS_MAX_H = 560;

export default function ManualCropDialog({
  open, onClose, studentId, fileId, filename, initialConfig,
  apiBase, // V1.42.2 · Opcional: si se pasa, sustituye el path por defecto
           // `/students/{studentId}/files/{fileId}`. Permite reutilizar el
           // diálogo con otras entidades (ej. `/accounting/expenses/{id}`).
  srcDataUrl,  // V1.77 · MODO LOCAL: imagen aún no guardada (data URL). No hay
               // fichero en OneDrive, así que el recorte se aplica de verdad
               // sobre la imagen y se devuelve por `onApply`.
  onApply,     // V1.77 · (dataUrl, crop_config) en modo local
  onSaved,
}) {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [detecting, setDetecting] = useState(false);
  const [blobUrl, setBlobUrl] = useState(null);
  const [imgSize, setImgSize] = useState({ w: 0, h: 0 });
  // Esquinas en COORDENADAS DE IMAGEN ORIGINAL (píxeles): [tl, tr, br, bl]
  const [corners, setCorners] = useState(null);
  const [filter, setFilter] = useState("none");
  const [rotation, setRotation] = useState(0);
  const [mode, setMode] = useState("original"); // "original" | "manual"
  const [dragIdx, setDragIdx] = useState(-1);
  const canvasRef = useRef(null);
  const imgRef = useRef(null);
  const panRef = useRef(null);      // V1.77.2 · contenedor con scroll (pan)
  const panStart = useRef(null);
  const base = apiBase || (studentId && fileId ? `/students/${studentId}/files/${fileId}` : "");
  const local = !!srcDataUrl;

  // ── Carga imagen + config existente ──────────────────────────────
  useEffect(() => {
    if (!open || (!base && !local)) return;
    let alive = true;
    (async () => {
      setLoading(true);
      setZoom(1);
      try {
        let url = srcDataUrl;
        if (!local) {
          const resp = await api.get(`${base}/download`, { responseType: "blob" });
          if (!alive) return;
          url = URL.createObjectURL(resp.data);
        }
        setBlobUrl(url);
        // Cargar dimensiones reales
        const img = new Image();
        img.onload = () => {
          if (!alive) return;
          setImgSize({ w: img.naturalWidth, h: img.naturalHeight });
          imgRef.current = img;
          // Config inicial
          const cfg = initialConfig || null;
          if (cfg && cfg.mode === "manual" && Array.isArray(cfg.corners) && cfg.corners.length === 4) {
            setCorners(cfg.corners.map((p) => [p[0], p[1]]));
            setMode("manual");
          } else {
            // Sin config → esquinas por defecto: 10 % de margen desde los bordes
            const m = 0.05;
            setCorners([
              [img.naturalWidth * m,          img.naturalHeight * m],
              [img.naturalWidth * (1 - m),    img.naturalHeight * m],
              [img.naturalWidth * (1 - m),    img.naturalHeight * (1 - m)],
              [img.naturalWidth * m,          img.naturalHeight * (1 - m)],
            ]);
            setMode(cfg ? (cfg.mode || "original") : "original");
          }
          setFilter((cfg && cfg.filter) || "none");
          setRotation((cfg && cfg.rotation) || 0);
        };
        img.onerror = () => {
          if (alive) toast.error("No se pudo cargar la imagen original");
        };
        img.src = url;
      } catch (e) {
        toast.error(formatApiError(e?.response?.data?.detail) || "Error al cargar imagen");
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => {
      alive = false;
      if (blobUrl && !local) URL.revokeObjectURL(blobUrl);
      setBlobUrl(null);
      setImgSize({ w: 0, h: 0 });
      setCorners(null);
      imgRef.current = null;
    };
     
  }, [open, studentId, fileId, apiBase, srcDataUrl]);

  // ── Escalado imagen ↔ canvas ─────────────────────────────────────
  // V1.77.1 · El lienzo se adapta al viewport: en móvil (390 px) un ancho
  // fijo de 820 px desbordaba el diálogo y la imagen quedaba cortada.
  const [viewport, setViewport] = useState({ w: 1280, h: 800 });
  useEffect(() => {
    const read = () => setViewport({ w: window.innerWidth, h: window.innerHeight });
    read();
    window.addEventListener("resize", read);
    window.addEventListener("orientationchange", read);
    return () => {
      window.removeEventListener("resize", read);
      window.removeEventListener("orientationchange", read);
    };
  }, []);
  const fitScale = useMemo(() => {
    if (!imgSize.w || !imgSize.h) return 1;
    const maxW = Math.max(240, Math.min(CANVAS_MAX_W, viewport.w - 56));
    const maxH = Math.max(240, Math.min(CANVAS_MAX_H, viewport.h - 330));
    return Math.min(maxW / imgSize.w, maxH / imgSize.h, 1);
  }, [imgSize, viewport]);
  // V1.77.2 · Zoom + arrastre lateral: en el móvil, con la imagen ajustada a
  // pantalla las esquinas 2 y 3 quedaban fuera de alcance. Ahora se puede
  // reducir/ampliar y mover la imagen con el dedo.
  const [zoom, setZoom] = useState(1);
  const scale = fitScale * zoom;
  const viewMaxH = Math.max(240, Math.min(CANVAS_MAX_H, viewport.h - 330));
  const zoomIn = () => setZoom((z) => Math.min(4, +(z + 0.25).toFixed(2)));
  const zoomOut = () => setZoom((z) => Math.max(0.4, +(z - 0.25).toFixed(2)));

  // V1.42 · Filtro CSS y rotación aplicados EN VIVO al preview del canvas.
  //  · Filtro: `ctx.filter` antes de dibujar la imagen.
  //  · Rotación: transformamos el canvas al centro y rotamos; los handles
  //    se dibujan en las mismas coordenadas del documento (post-rotación).
  //  · Drag: convertimos el punto del ratón al espacio del documento
  //    aplicando la rotación inversa.
  const FILTER_CSS = {
    none: "none",
    grey: "grayscale(1) contrast(1.15) brightness(1.02)",
    bw:   "grayscale(1) contrast(1.6) brightness(1.15)",
  };
  const rot90 = rotation % 180 !== 0;
  const canvasW = Math.round((rot90 ? imgSize.h : imgSize.w) * scale);
  const canvasH = Math.round((rot90 ? imgSize.w : imgSize.h) * scale);

  // Convierte coords del canvas (rotadas + escaladas) a coords del documento
  // original (sin rotar). Se usa en el drag para persistir en original.
  const canvasToDoc = (x, y) => {
    const cx = canvasW / 2;
    const cy = canvasH / 2;
    const dx = x - cx;
    const dy = y - cy;
    const rad = -(rotation * Math.PI) / 180;
    const rx = dx * Math.cos(rad) - dy * Math.sin(rad);
    const ry = dx * Math.sin(rad) + dy * Math.cos(rad);
    const iw = imgSize.w * scale;
    const ih = imgSize.h * scale;
    return { x: (rx + iw / 2) / scale, y: (ry + ih / 2) / scale };
  };
  // Inversa: coords del documento a coords del canvas rotado.
  const docToCanvas = (dx0, dy0) => {
    const iw = imgSize.w * scale;
    const ih = imgSize.h * scale;
    const px = dx0 * scale - iw / 2;
    const py = dy0 * scale - ih / 2;
    const rad = (rotation * Math.PI) / 180;
    const rx = px * Math.cos(rad) - py * Math.sin(rad);
    const ry = px * Math.sin(rad) + py * Math.cos(rad);
    return { x: rx + canvasW / 2, y: ry + canvasH / 2 };
  };

  // ── Render canvas ────────────────────────────────────────────────
  useEffect(() => {
    const c = canvasRef.current;
    if (!c || !imgRef.current || !corners) return;
    c.width = canvasW;
    c.height = canvasH;
    const ctx = c.getContext("2d");
    ctx.clearRect(0, 0, canvasW, canvasH);
    // Dibuja la imagen aplicando filtro CSS + rotación centrada.
    ctx.save();
    ctx.filter = FILTER_CSS[filter] || "none";
    ctx.translate(canvasW / 2, canvasH / 2);
    ctx.rotate((rotation * Math.PI) / 180);
    const iw = imgSize.w * scale;
    const ih = imgSize.h * scale;
    ctx.drawImage(imgRef.current, -iw / 2, -ih / 2, iw, ih);
    ctx.restore();
    if (mode === "manual") {
      // Handles: proyectamos las esquinas doc → canvas rotado.
      const pts = corners.map(([x, y]) => {
        const p = docToCanvas(x, y);
        return [p.x, p.y];
      });
      // Overlay oscuro fuera del polígono
      ctx.save();
      ctx.fillStyle = "rgba(0,0,0,0.45)";
      ctx.beginPath();
      ctx.rect(0, 0, canvasW, canvasH);
      ctx.moveTo(pts[0][0], pts[0][1]);
      for (let i = pts.length - 1; i >= 0; i--) ctx.lineTo(pts[i][0], pts[i][1]);
      ctx.closePath();
      ctx.fill("evenodd");
      ctx.restore();
      // Línea del polígono
      ctx.strokeStyle = "#22c55e";
      ctx.lineWidth = 2.5;
      ctx.beginPath();
      ctx.moveTo(pts[0][0], pts[0][1]);
      for (let i = 1; i < pts.length; i++) ctx.lineTo(pts[i][0], pts[i][1]);
      ctx.closePath();
      ctx.stroke();
      // Handles (círculos verdes con borde blanco)
      for (let i = 0; i < pts.length; i++) {
        const [x, y] = pts[i];
        ctx.beginPath();
        ctx.arc(x, y, HANDLE_SIZE, 0, Math.PI * 2);
        ctx.fillStyle = i === dragIdx ? "#facc15" : "#22c55e";
        ctx.fill();
        ctx.lineWidth = 3;
        ctx.strokeStyle = "#ffffff";
        ctx.stroke();
        ctx.fillStyle = "#ffffff";
        ctx.font = "bold 11px system-ui";
        ctx.textAlign = "center";
        ctx.textBaseline = "middle";
        ctx.fillText(String(i + 1), x, y);
      }
    }
     
  }, [corners, canvasW, canvasH, scale, dragIdx, mode, filter, rotation]);

  // ── Interacción arrastre ─────────────────────────────────────────
  const getPointerPos = (e) => {
    const c = canvasRef.current;
    const rect = c.getBoundingClientRect();
    const clientX = e.touches ? e.touches[0].clientX : e.clientX;
    const clientY = e.touches ? e.touches[0].clientY : e.clientY;
    return { x: clientX - rect.left, y: clientY - rect.top };
  };
  const onPointerDown = (e) => {
    if (mode !== "manual" || !corners) return;
    const { x, y } = getPointerPos(e);
    // V1.42 · Comparamos contra la proyección canvas-rotado de cada esquina.
    for (let i = 0; i < corners.length; i++) {
      const p = docToCanvas(corners[i][0], corners[i][1]);
      const d2 = (p.x - x) ** 2 + (p.y - y) ** 2;
      if (d2 <= (HANDLE_SIZE + 10) ** 2) {
        setDragIdx(i);
        e.preventDefault();
        return;
      }
    }
    // V1.77.2 · Lejos de una esquina → se arrastra la imagen (pan lateral).
    const box = panRef.current;
    if (box) {
      const cx = e.touches ? e.touches[0].clientX : e.clientX;
      const cy = e.touches ? e.touches[0].clientY : e.clientY;
      box.dataset.panning = "1";
      panStart.current = { cx, cy, left: box.scrollLeft, top: box.scrollTop };
      e.preventDefault();
    }
  };
  const onPointerMove = (e) => {
    const box = panRef.current;
    if (box && box.dataset.panning === "1" && panStart.current) {
      const cx = e.touches ? e.touches[0].clientX : e.clientX;
      const cy = e.touches ? e.touches[0].clientY : e.clientY;
      box.scrollLeft = panStart.current.left - (cx - panStart.current.cx);
      box.scrollTop = panStart.current.top - (cy - panStart.current.cy);
      e.preventDefault();
      return;
    }
    if (dragIdx < 0 || !corners) return;
    const { x, y } = getPointerPos(e);
    // V1.42 · Convertimos coords del canvas rotado al espacio del documento.
    const p = canvasToDoc(x, y);
    const nx = Math.max(0, Math.min(imgSize.w, p.x));
    const ny = Math.max(0, Math.min(imgSize.h, p.y));
    setCorners((prev) => {
      const next = prev.map((p2) => [...p2]);
      next[dragIdx] = [nx, ny];
      return next;
    });
    e.preventDefault();
  };
  const onPointerUp = () => {
    setDragIdx(-1);
    if (panRef.current) panRef.current.dataset.panning = "0";
    panStart.current = null;
  };

  // ── Acciones ─────────────────────────────────────────────────────
  const doAutoDetect = async () => {
    setDetecting(true);
    try {
      // V1.77 · En modo local la imagen aún no existe en OneDrive: detectamos
      // los bordes enviando la propia foto al pipeline de contabilidad.
      const { data } = local
        ? await api.post("/accounting/expenses/preprocess-scan",
                         { image_data_url: srcDataUrl, scan_filter: "original" })
        : await api.post(`${base}/detect-corners`);
      const pts = local
        ? (data.detected && Array.isArray(data.corners)
            ? data.corners.map((p) => [p.x * imgSize.w, p.y * imgSize.h]) : null)
        : (data.detected && data.corners ? data.corners.map(([x, y]) => [x, y]) : null);
      if (pts) {
        setCorners(pts);
        setMode("manual");
        toast.success("Bordes detectados — ajusta si es necesario.");
      } else {
        toast.info("No se pudo auto-detectar. Ajusta manualmente las esquinas.");
      }
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "Error auto-detección");
    } finally {
      setDetecting(false);
    }
  };
  const doUseOriginal = async () => {
    if (local) {
      onApply && onApply(srcDataUrl, null);
      onClose && onClose();
      return;
    }
    setSaving(true);
    try {
      await api.delete(`${base}/crop-config`);
      toast.success("Guardado: se usará la imagen original en el PDF");
      onSaved && onSaved(null);
      onClose && onClose();
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "Error");
    } finally {
      setSaving(false);
    }
  };
  const doSaveManual = async () => {
    if (mode === "manual" && (!corners || corners.length !== 4)) {
      toast.error("Ajusta las 4 esquinas antes de guardar");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        mode,
        corners: mode === "manual" ? corners : null,
        filter,
        rotation,
      };
      if (local) {
        // V1.77 · Se aplica el recorte de verdad y se devuelve la imagen.
        if (mode !== "manual") {
          onApply && onApply(srcDataUrl, null);
        } else {
          const { data } = await api.post("/accounting/expenses/apply-crop", {
            image_data_url: srcDataUrl, mode, corners, filter, rotation,
          });
          onApply && onApply(data.image_data_url, data.crop_config);
          toast.success("Recorte aplicado a la foto");
        }
        onClose && onClose();
        return;
      }
      const { data } = await api.patch(`${base}/crop-config`, payload);
      toast.success("Recorte guardado. El PDF se generará con esta configuración.");
      onSaved && onSaved(data.crop_config);
      onClose && onClose();
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "Error al guardar");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose && onClose()}>
      <DialogContent
        /* V1.77.1 · Se abre por encima de modales propios (z-[70]) como
           "Nuevo gasto" o la ficha del gasto; si no, quedaba detrás. */
        className="w-[calc(100vw-1rem)] sm:w-full max-w-[900px] p-0 z-[95] max-h-[92vh] overflow-y-auto overflow-x-hidden"
        overlayClassName="z-[90]"
        data-testid="manual-crop-dialog"
      >
        <DialogHeader className="px-5 pt-4 pb-2 border-b border-neutral-200">
          <DialogTitle className="text-sm font-bold flex items-center gap-2">
            <Scan className="h-4 w-4 text-indigo-600" />
            Ajustar recorte · {filename || "documento"}
          </DialogTitle>
          <p className="text-[11px] text-neutral-500 mt-0.5">
            {local
              ? "Encuadra el documento antes de enviarlo al OCR. La foto original de tu dispositivo no se altera."
              : "La imagen original nunca se modifica — sólo se guardan las esquinas."}
            <b> Regla:</b> ORIGINAL &gt; MANUAL &gt; AUTOMATISMOS.
          </p>
        </DialogHeader>

        <div className="px-5 py-3 space-y-3 min-w-0">
          {/* Modo */}
          <div className="flex items-center gap-1.5 flex-wrap">
            <span className="text-[11px] font-semibold text-neutral-700 mr-1">Modo:</span>
            {[
              { key: "original", label: "Usar original", icon: ImageOff },
              { key: "manual",   label: "Recortar 4 esquinas", icon: Scan },
            ].map(({ key, label, icon: Icon }) => (
              <button
                key={key}
                type="button"
                onClick={() => setMode(key)}
                className={
                  "inline-flex items-center gap-1.5 h-7 px-2.5 rounded-full text-[11px] font-semibold border transition-colors " +
                  (mode === key
                    ? "bg-indigo-600 text-white border-indigo-600"
                    : "bg-white text-neutral-700 border-neutral-300 hover:bg-neutral-50")
                }
                data-testid={`crop-mode-${key}`}
              >
                <Icon className="h-3.5 w-3.5" /> {label}
              </button>
            ))}
            <div className="flex-1" />
            {mode === "manual" && (
              <button
                type="button"
                onClick={doAutoDetect}
                disabled={detecting}
                className="inline-flex items-center gap-1.5 h-7 px-2.5 rounded-full text-[11px] font-semibold border border-amber-300 bg-amber-50 text-amber-800 hover:bg-amber-100 disabled:opacity-50"
                title="Sugerir esquinas automáticamente (puede fallar en fondos difíciles)"
                data-testid="crop-auto-detect"
              >
                {detecting ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Sparkles className="h-3.5 w-3.5" />}
                Auto-detectar (opcional)
              </button>
            )}
          </div>

          {/* Zoom (V1.77.2 · imprescindible en móvil para alcanzar las esquinas) */}
          <div className="flex items-center gap-1.5">
            <span className="text-[11px] font-semibold text-neutral-700">Zoom:</span>
            <button type="button" onClick={zoomOut}
              className="h-7 w-8 rounded-full border border-neutral-300 bg-white text-neutral-700 text-sm font-bold hover:bg-neutral-50"
              data-testid="crop-zoom-out">−</button>
            <span className="text-[11px] font-mono w-10 text-center" data-testid="crop-zoom-level">
              {Math.round(zoom * 100)}%
            </span>
            <button type="button" onClick={zoomIn}
              className="h-7 w-8 rounded-full border border-neutral-300 bg-white text-neutral-700 text-sm font-bold hover:bg-neutral-50"
              data-testid="crop-zoom-in">+</button>
            <button type="button" onClick={() => setZoom(1)}
              className="h-7 px-2.5 rounded-full border border-neutral-300 bg-white text-[11px] font-semibold text-neutral-700 hover:bg-neutral-50"
              data-testid="crop-zoom-fit">Ajustar a pantalla</button>
          </div>

          {/* Canvas */}
          <div
            ref={panRef}
            className="relative w-full min-w-0 max-w-full bg-neutral-100 rounded-lg overflow-auto select-none"
            style={{ minHeight: 240, maxHeight: viewMaxH, touchAction: "none" }}
          >
            {loading || !blobUrl ? (
              <div className="py-12 text-neutral-500 text-sm inline-flex items-center gap-2 w-full justify-center">
                <Loader2 className="h-4 w-4 animate-spin" /> Cargando imagen…
              </div>
            ) : (
              /* V1.77.2 · `w-max` + `mx-auto`: centra sin romper el scroll
                 horizontal (con flex+justify-center el lado izquierdo queda
                 inalcanzable al ampliar). */
              <div className="w-max mx-auto">
              <canvas
                ref={canvasRef}
                onMouseDown={onPointerDown}
                onMouseMove={onPointerMove}
                onMouseUp={onPointerUp}
                onMouseLeave={onPointerUp}
                onTouchStart={onPointerDown}
                onTouchMove={onPointerMove}
                onTouchEnd={onPointerUp}
                style={{
                  cursor: mode === "manual" ? (dragIdx >= 0 ? "grabbing" : "crosshair") : "default",
                  touchAction: "none",
                  display: "block",
                }}
                data-testid="crop-canvas"
              />
              </div>
            )}
          </div>

          {/* Filtros + rotación (opcionales, sólo si modo manual) */}
          {mode === "manual" && (
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-[11px] font-semibold text-neutral-700">Filtro (opcional):</span>
              {[
                { key: "none", label: "Ninguno (colores originales)" },
                { key: "grey", label: "Grises + realce suave" },
                { key: "bw",   label: "Fotocopia (fondo blanco)" },
              ].map(({ key, label }) => (
                <button
                  key={key}
                  type="button"
                  onClick={() => setFilter(key)}
                  className={
                    "h-7 px-2.5 rounded-full text-[11px] font-semibold border transition-colors " +
                    (filter === key
                      ? "bg-neutral-900 text-white border-neutral-900"
                      : "bg-white text-neutral-700 border-neutral-300 hover:bg-neutral-50")
                  }
                  data-testid={`crop-filter-${key}`}
                >
                  {label}
                </button>
              ))}
              <div className="flex-1" />
              <button
                type="button"
                onClick={() => setRotation((r) => (r + 90) % 360)}
                className="inline-flex items-center gap-1 h-7 px-2.5 rounded-full text-[11px] font-semibold border border-neutral-300 bg-white text-neutral-700 hover:bg-neutral-50"
                title="Rotar 90° en sentido horario (aplica al recorte final)"
                data-testid="crop-rotate"
              >
                <RotateCw className="h-3.5 w-3.5" /> Rotar {rotation ? `(${rotation}°)` : ""}
              </button>
            </div>
          )}

          <p className="text-[10px] text-neutral-500 italic">
            {mode === "manual"
              ? "Arrastra las 4 esquinas (1 superior-izq → 4 inferior-izq). Arrastrando fuera de las esquinas mueves la imagen; con − y + la reduces o amplías."
              : "Se usará la imagen tal cual en el PDF, sin recortes ni filtros."}
          </p>
        </div>

        <DialogFooter className="px-5 py-3 border-t border-neutral-200 flex-row flex-wrap items-center justify-between gap-2">
          <Button
            variant="ghost"
            onClick={doUseOriginal}
            disabled={saving || loading}
            className="text-[11px]"
            data-testid="crop-use-original-btn"
          >
            <Undo2 className="h-3.5 w-3.5 mr-1" /> {local ? "Usar foto sin recortar" : "Quitar recorte (usar original)"}
          </Button>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              onClick={() => onClose && onClose()}
              disabled={saving}
              className="text-[11px]"
              data-testid="crop-cancel-btn"
            >Cancelar</Button>
            <Button
              onClick={doSaveManual}
              disabled={saving || loading}
              className="text-[11px] bg-indigo-600 hover:bg-indigo-700"
              data-testid="crop-save-btn"
            >
              {saving ? <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" /> : <Save className="h-3.5 w-3.5 mr-1" />}
              {local ? "Aplicar recorte" : "Guardar"}
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
